"use client";

import React from "react";
import { Badge, Button, Input, Tooltip, TooltipTrigger, TooltipContent } from "@heroui/react";
// useClipboard removed in v3 — using local state instead
import { Icon } from "@iconify/react";
import { cn } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { ToolCallExecution, Usage } from "@webuild-ai/admin/models/components";
import { useBrand } from "@webuild-ai/react-auth";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { FilePreviewModal } from "@/components/FilePreview/FilePreviewModal";
import { UploadedFile, useChatStore } from "@/stores/ChatStore";
import { useArtefactStore } from "@/stores/ArtefactStore";
import { Citation } from "@/types/citations";
import { CitationDocumentViewer } from "@/components/Citations/CitationDocumentViewer";
import { useSpeechStore } from "@/stores/SpeechStore";
import { convertFileTypeToIcon } from "@/utils/ConvertFileTypeToIcon";
import { FileTypeIcon } from "@/components/UI/FileTypeIcon";
import { MarkdownWeBuild } from "@/components/MarkdownWeBuild";
import { useTheme } from "@/hooks/use-theme";
import { useRegeneration } from "@/hooks/useRegeneration";
import { inferMimeFromFilename } from "@/utils/fileUtils";
import { useAuthenticatedFileFetcher } from "@/hooks/chat/useAuthenticatedFileFetcher";
import {
  parseExcelDownload,
  removeExcelDownloadMarker,
  downloadExcelFile,
  ExcelDownloadData,
} from "@/utils/excelDownloadUtils";
import {
  parseDocxDownload,
  removeDocxDownloadMarker,
  downloadDocxFile,
  DocxDownloadData,
} from "@/utils/docxDownloadUtils";
import { logger } from "@/telemetry";

import { MarkdownWithCitations } from "./MarkdownWithCitations";
import { AttemptNavigation } from "./AttemptNavigation";
import { RegenerateButton } from "./RegenerateButton";
import { MessageSources, type SourceFile, type SourceWeb } from "./MessageSources";

export type MessageCardProps = React.HTMLAttributes<HTMLDivElement> & {
  avatar?: string;
  userName?: string;
  showFeedback?: boolean;
  message?:
    | string
    | {
        [key: string]:
          | string
          | string[]
          | { [key: string]: string }
          | Array<{ [key: string]: string }>;
      };
  liked: boolean | null | undefined;
  messageID: string;
  agent?: string;
  currentAttempt?: number;
  status: "success" | "failed" | "streaming" | "stopped";
  runId: string;
  attempts?: number;
  toolCallExecutions?: ToolCallExecution[];
  usage?: Usage;
  messageClassName?: string;
  dataId?: string;
  timestamp?: string;
  citations?: any[]; // Citations from structured citations feature
  onMessageCopy?: (content: string | string[]) => void;
  onFeedback?: (liked: boolean, textFeedback: string | undefined) => void;
  onAttemptFeedback?: (feedback: "like" | "dislike" | "same") => void;
  onSavePrompt?: (prompt: string) => void;
  onRegenerate?: () => Promise<void>;
  onAttemptChange?: (attempt: number) => void;
};

const copyToClipboard = (element: HTMLElement, message: string) => {
  if (element) {
    const htmlContent = element.innerHTML;
    const textContent = element.textContent || message;

    try {
      const htmlBlob = new Blob([htmlContent], { type: "text/html" });
      const textBlob = new Blob([textContent], { type: "text/plain" });

      const item = new ClipboardItem({
        "text/html": htmlBlob,
        "text/plain": textBlob,
      });

      navigator.clipboard
        .write([item])
        .then(() => {
          logger.info("Formatted text copied to clipboard");
        })
        .catch((err) => {
          logger.error("Failed to copy formatted text, falling back to plain text", err);
          fallbackCopyText(textContent);
        });
    } catch {
      fallbackCopyText(textContent);
    }
  } else {
    fallbackCopyText(message);
  }
};

const fallbackCopyText = (text: string) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      logger.info("Text copied to clipboard (plain text fallback)");
    })
    .catch((err) => {
      logger.error("Failed to copy text", err);
    });
};

const MessageCard = React.forwardRef<HTMLDivElement, MessageCardProps>(
  (
    {
      avatar,
      userName,
      message,
      toolCallExecutions,
      usage: _usage,
      messageID,
      liked,
      dataId,
      timestamp,
      showFeedback,
      attempts: _attempts = 1,
      currentAttempt: _currentAttempt = 1,
      agent,
      runId,
      status,
      citations,
      onMessageCopy,
      onAttemptChange,
      onFeedback,
      onAttemptFeedback: _onAttemptFeedback,
      onSavePrompt,
      onRegenerate,
      className,
      messageClassName,
      ...props
    },
    ref,
  ) => {
    if (toolCallExecutions && toolCallExecutions.length > 0) {
      logger.info("toolCallExecutions", { count: toolCallExecutions.length });
    }
    const [feedback, setFeedback] = React.useState<"like" | "dislike" | undefined>(
      liked === undefined || liked === null ? undefined : liked ? "like" : "dislike",
    );
    const [userFeedbackText, setUserFeedbackText] = React.useState<string>("");
    const [requestTextFeedback, setRequestTextFeedback] = React.useState<boolean>(false);
    const { t } = useTranslation();
    const { theme } = useTheme();
    const { brand } = useBrand();
    const messageRef = React.useRef<HTMLDivElement>(null);
    const [copied, setCopied] = React.useState(false);
    const { adminClient, platformClient } = useWebuildAPIClient();
    const { submitFeedback, stopStreaming, chat, chatWorkflowId } = useChatStore();
    const { navigateToArtefact } = useArtefactStore();
    const { speak, stopSpeech, speakingMessageId } = useSpeechStore();
    const fileFetcher = useAuthenticatedFileFetcher();
    const downloadFetcher = useAuthenticatedFileFetcher();

    // Initialize regeneration hook
    const regenerationHook = useRegeneration(runId, messageID);
    const [sourcesFiles, setSourcesFiles] = React.useState<SourceFile[]>([]);
    const [sourcesWeb, setSourcesWeb] = React.useState<SourceWeb[]>([]);
    // File preview state
    const [previewOpen, setPreviewOpen] = React.useState(false);
    const [previewLoading, setPreviewLoading] = React.useState(false);
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
    const [previewError, setPreviewError] = React.useState<string | null>(null);
    const [previewFile, setPreviewFile] = React.useState<UploadedFile | null>(null);
    const [previewDocumentData, setPreviewDocumentData] = React.useState<ArrayBuffer | null>(null);
    const previewAbortRef = React.useRef<AbortController | null>(null);
    const isMountedRef = React.useRef(false);
    const previewUrlRef = React.useRef(previewUrl);
    const fileFetcherRef = React.useRef(fileFetcher);

    previewUrlRef.current = previewUrl;
    fileFetcherRef.current = fileFetcher;
    // Citation viewer state
    const [citationViewerOpen, setCitationViewerOpen] = React.useState(false);
    const [selectedCitation, setSelectedCitation] = React.useState<Citation | null>(null);

    // Use memoized string version to detect actual content changes
    const toolCallExecutionsKey = React.useMemo(
      () => JSON.stringify(toolCallExecutions || []),
      [toolCallExecutions],
    );

    React.useEffect(() => {
      const newSourcesFiles: SourceFile[] = [];
      const newSourcesWeb: SourceWeb[] = [];

      if (toolCallExecutions && toolCallExecutions.length > 0) {
        for (const toolCallExecution of toolCallExecutions) {
          if (toolCallExecution.name == "knowledge_base_retriever") {
            let args = toolCallExecution.args;

            for (const id of Object.keys(args)) {
              const data = args[id].data;

              if (!args[id].metadata) {
                logger.error("invalid semantic search tool call response", undefined, { id });
                continue;
              }
              const filename = args[id].metadata.name;
              const file_type = args[id].metadata.file_type;
              const uploaded_by = args[id].metadata.uploaded_by;
              const uploaded_timestamp = args[id].metadata.uploaded_timestamp;
              const file_id = args[id].metadata.id;

              let timestamp = "";

              if (uploaded_timestamp) {
                timestamp = new Date(uploaded_timestamp).toLocaleString();
              } else {
                timestamp = "";
              }

              newSourcesFiles.push({
                id: file_id,
                title: filename,
                summary: data,
                icon: convertFileTypeToIcon(file_type),
                timestamp: timestamp,
                uploaded_by: uploaded_by,
              } as SourceFile);
            }
          }
          if (
            toolCallExecution.name === "web_search" ||
            toolCallExecution.name === "web_search_serpapi" ||
            toolCallExecution.name === "web_search_tavily" ||
            toolCallExecution.name === "crawler"
          ) {
            const args = toolCallExecution.args;
            let jsonReadyObject: any[] = [];

            if (args.data && Array.isArray(args.data)) {
              jsonReadyObject = args.data;
            } else if (args.data && typeof args.data === "string") {
              // Only do string replacements if it's actually a string
              const jsonReady = args.data
                .replace(/'/g, '"') // Replace single quotes with double quotes
                .replace(/\bNone\b/g, "null"); // Replace Python None with null

              try {
                jsonReadyObject = JSON.parse(jsonReady);
              } catch {
                jsonReadyObject = [];
              }
            } else if (Array.isArray(args)) {
              jsonReadyObject = args;
            } else {
              jsonReadyObject = [];
            }

            if (Array.isArray(jsonReadyObject)) {
              for (const site of jsonReadyObject) {
                if (
                  !site.url ||
                  !site.title ||
                  (!site.content?.snippet && !site.content?.content && !site.content)
                ) {
                  continue;
                }
                const hostname = new URL(site.url).hostname;

                newSourcesWeb.push({
                  title: site.title,
                  summary: site.content.snippet || site.content.content || site.content,
                  favicon: `https://${hostname}/favicon.ico`,
                  url: site.url,
                });
              }
            }
          }
        }
      }

      setSourcesFiles(newSourcesFiles);
      setSourcesWeb(newSourcesWeb);
    }, [toolCallExecutions, toolCallExecutionsKey, messageID, status]);

    let messageContent = message;
    let files: UploadedFile[] = [];
    let isAgent = agent !== "";

    if (typeof message === "object") {
      messageContent = typeof message.prompt === "string" ? message.prompt : "";
      if (message.files && Array.isArray(message.files)) {
        files = message.files.map((file: any) => ({
          name: file.name,
          size: file.size,
          type: file.type,
          id: file.id,
        }));
      }
    } else {
      messageContent = message;
    }

    // Parse Excel download marker from message content (if present)
    // Memoized to avoid re-parsing on every render for long messages
    const excelDownload: ExcelDownloadData | null = React.useMemo(
      () => (typeof messageContent === "string" ? parseExcelDownload(messageContent) : null),
      [messageContent],
    );

    // Parse DOCX download marker from message content (if present)
    // Memoized to avoid re-parsing on every render for long messages
    const docxDownload: DocxDownloadData | null = React.useMemo(
      () => (typeof messageContent === "string" ? parseDocxDownload(messageContent) : null),
      [messageContent],
    );

    // State for download error feedback (separate per file type to avoid cross-contamination)
    const [excelDownloadError, setExcelDownloadError] = React.useState<string | null>(null);
    const [docxDownloadError, setDocxDownloadError] = React.useState<string | null>(null);

    // Remove the markers from displayed content
    if (excelDownload && typeof messageContent === "string") {
      messageContent = removeExcelDownloadMarker(messageContent);
    }
    if (docxDownload && typeof messageContent === "string") {
      messageContent = removeDocxDownloadMarker(messageContent);
    }

    // Fallback: if no files were passed via props, try to fetch from store (top-level files, attachments, or content.files)
    if ((!files || files.length === 0) && chat && Array.isArray(chat.messages)) {
      const storeMsg = (chat.messages as any[]).find(
        (m: any) => String(m.id) === String(messageID),
      );

      if (storeMsg) {
        const topLevelFiles = Array.isArray(storeMsg.files) ? storeMsg.files : [];
        const attachments = Array.isArray(storeMsg.attachments) ? storeMsg.attachments : [];
        const contentFiles =
          storeMsg && storeMsg.content && Array.isArray(storeMsg.content?.files)
            ? storeMsg.content.files
            : [];
        const rawFiles =
          topLevelFiles.length > 0
            ? topLevelFiles
            : attachments.length > 0
              ? attachments
              : contentFiles;

        files = Array.isArray(rawFiles)
          ? rawFiles
              .map((f: any) => ({
                id: f.id ?? f.file_id ?? f.uuid ?? String(f.name || f.filename || ""),
                name: f.name ?? f.filename ?? "",
                type: f.type ?? f.mime_type ?? f.file_type ?? "application/octet-stream",
                size: String(f.size ?? f.filesize ?? ""),
              }))
              .filter((f: any) => f.id || f.name)
          : [];
      }
    }

    const failedMessageClassName =
      status === "failed" ? "bg-danger-100/50 border border-danger-100 text-foreground" : "";

    const agentMessageClassName = isAgent
      ? ""
      : `${theme === "light" ? "bg-brand-secondary" : "bg-content2"}`;

    const failedMessage = (error: string) => (
      <p>
        {t("Something went wrong, if the issue persists please contact us")} - {error}
      </p>
    );

    const handleStop = () => {
      logger.info("Stopping");
      if (!adminClient) {
        throw new Error("Missing adminClient");
      }
      stopStreaming(adminClient, platformClient ?? undefined, false);
    };

    const handleRegenerate = async () => {
      if (onRegenerate) {
        // If parent provides onRegenerate, use that (for backward compatibility)
        try {
          await onRegenerate();
        } catch (error) {
          logger.error("Regeneration failed", error);
        }
      } else {
        // Otherwise use the hook
        await regenerationHook.regenerate();
      }
    };

    const handleSavePrompt = () => {
      if (typeof messageContent === "string" && onSavePrompt) {
        onSavePrompt(messageContent);
      }
    };

    const handleAttemptChange = async (attemptNumber: number) => {
      if (onAttemptChange) {
        // If parent provides onAttemptChange, use that (for backward compatibility)
        onAttemptChange(attemptNumber);
      } else {
        // Otherwise use the hook
        await regenerationHook.switchAttempt(attemptNumber);
      }
    };

    const revokePreviewUrl = React.useCallback(() => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch {
          // Ignore revoke errors
        }
      }
      setPreviewUrl(null);
    }, [previewUrl]);

    const handlePreviewOpenChange = (open: boolean) => {
      setPreviewOpen(open);
      if (!open) {
        try {
          if (previewAbortRef.current) {
            previewAbortRef.current.abort();
            previewAbortRef.current = null;
          }
        } catch {
          // Ignore abort errors
        }
        revokePreviewUrl();
        setPreviewError(null);
        setPreviewFile(null);
        setPreviewDocumentData(null);
      }
    };

    // Sync hook state with component state
    React.useEffect(() => {
      if (fileFetcher.data.arrayBuffer) {
        setPreviewDocumentData(fileFetcher.data.arrayBuffer);
        if (fileFetcher.data.mimeType) {
          setPreviewFile((p) => (p ? { ...p, type: fileFetcher.data.mimeType! } : p));
        }
      } else if (fileFetcher.data.blobUrl) {
        setPreviewUrl(fileFetcher.data.blobUrl);
        if (fileFetcher.data.mimeType) {
          setPreviewFile((p) => (p ? { ...p, type: fileFetcher.data.mimeType! } : p));
        }
      }
    }, [fileFetcher.data]);

    // Sync hook error state
    React.useEffect(() => {
      if (fileFetcher.error) {
        setPreviewError(fileFetcher.error);
      }
    }, [fileFetcher.error]);

    // Sync hook loading state
    React.useEffect(() => {
      setPreviewLoading(fileFetcher.loading);
    }, [fileFetcher.loading]);

    // Handle download when blob URL is ready
    React.useEffect(() => {
      if (downloadFetcher.data.blobUrl && previewFile) {
        // Trigger download
        const a = document.createElement("a");

        a.href = downloadFetcher.data.blobUrl;
        a.download = previewFile.name || "download";
        document.body.appendChild(a);
        a.click();
        a.remove();

        // Clean up blob URL
        setTimeout(() => {
          downloadFetcher.cleanup();
        }, 100);
      }
      // TODO: Stabilise downloadFetcher ref so the full dependency array can be used
      // eslint-disable-next-line react-hooks/exhaustive-deps -- downloadFetcher is an unstable object ref; using blobUrl as the trigger
    }, [downloadFetcher.data.blobUrl]);

    React.useEffect(() => {
      isMountedRef.current = true;

      return () => {
        isMountedRef.current = false;
        try {
          if (previewAbortRef.current) {
            previewAbortRef.current.abort();
            previewAbortRef.current = null;
          }
        } catch {
          // Ignore abort errors
        }
        const url = previewUrlRef.current;

        if (url && url.startsWith("blob:")) {
          try {
            URL.revokeObjectURL(url);
          } catch {
            // Ignore revoke errors
          }
        }
        // Cleanup hook's blob URL
        fileFetcherRef.current.cleanup();
      };
    }, []);

    const openPreviewForFile = async (file: UploadedFile) => {
      try {
        // Abort previous request
        if (previewAbortRef.current) {
          try {
            previewAbortRef.current.abort();
          } catch {
            // Ignore abort errors
          }
        }
        const controller = new AbortController();

        previewAbortRef.current = controller;

        // Set loading state
        if (isMountedRef.current) {
          setPreviewError(null);
          setPreviewLoading(true);
          setPreviewFile(file);
          setPreviewOpen(true);
        }

        // Detect file type for special handling
        const mime = inferMimeFromFilename(file.name, file.type || undefined);
        const isDocLike =
          mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
          mime === "application/msword" ||
          mime === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
          mime === "application/vnd.ms-powerpoint" ||
          mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
          mime === "application/vnd.ms-excel";
        const isPdf = mime === "application/pdf";

        // Use hook to fetch file with appropriate format
        await fileFetcher.fetchFile(file.id, file.name, {
          asArrayBuffer: isDocLike || isPdf,
          signal: controller.signal,
        });

        // Note: State updates will happen via useEffect watching fileFetcher.data
      } catch (e: any) {
        // Handle abort gracefully
        if (e?.name === "AbortError") {
          return;
        }

        logger.error("File preview failed", e);

        if (isMountedRef.current) {
          setPreviewError(
            e.message || t("Preview not available. Please try downloading the file."),
          );
        }
      } finally {
        if (isMountedRef.current) {
          setPreviewLoading(false);
        }
      }
    };

    const downloadPreviewFile = async () => {
      try {
        const file = previewFile;

        if (!file) return;

        // Fetch file as blob URL for download
        // The actual download will be triggered by the useEffect watching downloadFetcher.data.blobUrl
        await downloadFetcher.fetchFile(file.id, file.name, {
          asArrayBuffer: false,
        });
      } catch (e) {
        logger.error("Download failed", e);
        // Error is silently handled - could add user notification here
      }
    };

    const hasFailed = status === "failed";

    const formatTimestamp = React.useCallback((ts?: string) => {
      if (!ts) return "";
      const date = new Date(ts);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const time = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

      if (isToday) return time;
      const datePart = date.toLocaleDateString();

      return `${datePart} ${time}`;
    }, []);

    const renderMessage = (message: React.ReactNode) => {
      if (hasFailed) return failedMessage(message as string);
      if (typeof message !== "string") return <>{message}</>;

      // At this point we know message is a string
      const messageStr = message as string;

      if (messageStr.includes("```markdown")) {
        const matches = messageStr.match(/([\s\S]*?)```markdown([\s\S]*?)```([\s\S]*)?/);

        if (matches) {
          const textBeforeMarkdown = matches[1] || "";
          const markdownContent = matches[2].trim();
          const remainingText = matches[3] || "";

          return (
            <div data-hj-suppress data-id="message-card-markdown">
              {renderMessage(textBeforeMarkdown)}
              {markdownContent && (
                <div>
                  <br />
                  <MarkdownWeBuild>{markdownContent}</MarkdownWeBuild>
                  <br />
                </div>
              )}
              {renderMessage(remainingText)}
            </div>
          );
        }
      }

      // Replace code blocks and HTML content with user-friendly references
      const codeBlockPattern = /```(\w+)?\n?([\s\S]*?)```/g;
      const codeMatches = Array.from(messageStr.matchAll(codeBlockPattern));

      // Also detect raw HTML documents (not wrapped in code blocks)
      const rawHtmlWithDoctypePattern = /<!DOCTYPE\s+html>[\s\S]*?<html[\s\S]*?<\/html>/gi;
      const rawHtmlWithoutDoctypePattern = /<html[\s\S]*?<\/html>/gi;
      const rawHtmlWithDoctypeMatches = Array.from(messageStr.matchAll(rawHtmlWithDoctypePattern));
      const rawHtmlWithoutDoctypeMatches = Array.from(
        messageStr.matchAll(rawHtmlWithoutDoctypePattern),
      );

      // Combine all matches and sort by position
      const allMatches = [
        ...codeMatches.map((m) => ({ type: "codeblock" as const, match: m })),
        ...rawHtmlWithDoctypeMatches.map((m) => ({ type: "rawhtml" as const, match: m })),
        ...rawHtmlWithoutDoctypeMatches.map((m) => ({ type: "rawhtml" as const, match: m })),
      ].sort((a, b) => (a.match.index || 0) - (b.match.index || 0));

      // Filter out overlapping matches (keep first occurrence)
      const uniqueMatches = allMatches.filter((current, index) => {
        if (index === 0) return true;
        const currentStart = current.match.index || 0;
        const currentEnd = currentStart + current.match[0].length;

        // Check if this match overlaps with any previous match
        for (let i = 0; i < index; i++) {
          const prevStart = allMatches[i].match.index || 0;
          const prevEnd = prevStart + allMatches[i].match[0].length;

          // Check for overlap
          if (
            (currentStart >= prevStart && currentStart < prevEnd) ||
            (currentEnd > prevStart && currentEnd <= prevEnd) ||
            (currentStart <= prevStart && currentEnd >= prevEnd)
          ) {
            return false; // Overlapping, filter out
          }
        }

        return true;
      });

      if (uniqueMatches.length > 0) {
        // Split message into parts and render with JSX components
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;

        uniqueMatches.forEach(({ type, match }, index) => {
          const matchStart = match.index || 0;
          let fullMatch, language, content, lang;

          if (type === "codeblock") {
            // For code blocks: match[0] = full match, match[1] = language, match[2] = content
            [fullMatch, language = "", content] = match;
            lang = language.toLowerCase();
          } else {
            // For raw HTML: match[0] = full HTML content
            fullMatch = match[0];
            language = "html";
            content = fullMatch;
            lang = "html";
          }

          // Add text before this code block
          if (matchStart > lastIndex) {
            const textBefore = messageStr.substring(lastIndex, matchStart);

            if (textBefore.trim()) {
              // Use MarkdownWithCitations if citations exist to preserve clickable citations
              if (citations && citations.length > 0) {
                parts.push(
                  <MarkdownWithCitations
                    key={`text-${index}-before`}
                    citations={citations}
                    message={textBefore}
                    onCitationClick={(citation) => {
                      setSelectedCitation(citation);
                      setCitationViewerOpen(true);
                    }}
                  />,
                );
              } else {
                parts.push(
                  <MarkdownWeBuild key={`text-${index}-before`}>{textBefore}</MarkdownWeBuild>,
                );
              }
            }
          }

          // Determine content type and appropriate messaging
          let contentTypeKey = "Code";
          let icon = "💻";

          if (lang === "html" || content.includes("<html") || content.includes("canvas")) {
            if (
              content.includes("chart") ||
              content.includes("Chart") ||
              content.includes("canvas")
            ) {
              contentTypeKey = "Chart";
              icon = "📊";
            } else if (content.includes("table") || content.includes("<th")) {
              contentTypeKey = "Data Table";
              icon = "📋";
            } else {
              contentTypeKey = "HTML Content";
              icon = "🌐";
            }
          } else if (
            lang === "json" ||
            content.trim().startsWith("{") ||
            content.trim().startsWith("[")
          ) {
            contentTypeKey = "Data";
            icon = "📋";
          } else if (lang === "python") {
            contentTypeKey = "Python Code";
            icon = "🐍";
          } else if (lang === "javascript" || lang === "js") {
            contentTypeKey = "JavaScript Code";
            icon = "⚡";
          } else if (lang === "typescript" || lang === "ts") {
            contentTypeKey = "TypeScript Code";
            icon = "📘";
          } else if (lang === "sql") {
            contentTypeKey = "SQL Query";
            icon = "🗄️";
          } else if (lang === "markdown" || lang === "md") {
            contentTypeKey = "Document";
            icon = "📄";
          } else if (lang === "yaml" || lang === "yml") {
            contentTypeKey = "Configuration";
            icon = "⚙️";
          } else if (lang) {
            contentTypeKey = `${lang.toUpperCase()} Code`;
            icon = "💻";
          }

          const contentType = t(contentTypeKey);

          // Add the reference card as JSX - using Button for better interactivity
          parts.push(
            <Button
              key={`reference-${index}`}
              disableRipple
              className="my-4 px-4 py-3 h-auto w-full justify-start bg-default-100 dark:bg-default-100/50 border border-default-200 dark:border-default-700 hover:bg-default-200 dark:hover:bg-default-200/50 transition-[transform,background-color] duration-150 hover:scale-[1.01] [&>span]:w-full [&>span]:flex [&>span]:justify-start"
              variant="secondary"
              onPress={() => navigateToArtefact(messageID)}
            >
              <div className="w-full flex flex-col items-start">
                <div className="flex items-center gap-2 text-default-700 dark:text-default-300">
                  <span className="text-lg">{icon}</span>
                  <span className="font-medium">
                    {t("{{contentType}} generated", { contentType })}
                  </span>
                </div>
                <p className="text-sm text-default-600 dark:text-default-400 mt-1">
                  {t("Click to view")}
                </p>
              </div>
            </Button>,
          );

          lastIndex = matchStart + fullMatch.length;
        });

        // Add remaining text after last code block
        if (lastIndex < messageStr.length) {
          const remainingText = messageStr.substring(lastIndex);

          if (remainingText.trim()) {
            // Use MarkdownWithCitations if citations exist to preserve clickable citations
            if (citations && citations.length > 0) {
              parts.push(
                <MarkdownWithCitations
                  key="text-after"
                  citations={citations}
                  message={remainingText}
                  onCitationClick={(citation) => {
                    setSelectedCitation(citation);
                    setCitationViewerOpen(true);
                  }}
                />,
              );
            } else {
              parts.push(<MarkdownWeBuild key="text-after">{remainingText}</MarkdownWeBuild>);
            }
          }
        }

        return <div>{parts}</div>;
      }

      const cleanedMessage = messageStr.replace(/<br>/g, " ");

      // Use MarkdownWithCitations if citations exist to make [C1], [C2] clickable
      if (citations && citations.length > 0 && typeof message === "string") {
        return (
          <MarkdownWithCitations
            citations={citations}
            message={message}
            onCitationClick={(citation) => {
              setSelectedCitation(citation);
              setCitationViewerOpen(true);
            }}
          />
        );
      }

      return <MarkdownWeBuild>{cleanedMessage}</MarkdownWeBuild>;
    };

    const handleCopy = React.useCallback(() => {
      let stringValue = "";

      if (typeof message === "string") {
        stringValue = message;
      } else if (Array.isArray(message)) {
        (message as any[]).forEach((child: any) => {
          const childString =
            typeof child === "string" ? child : child?.props?.children?.toString();

          if (childString) {
            stringValue += childString + "\n";
          }
        });
      }

      const valueToCopy = stringValue || messageRef.current?.textContent || "";

      if (!messageRef.current) {
        throw Error("Message ref is null");
      }
      copyToClipboard(messageRef.current, valueToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onMessageCopy?.(valueToCopy);
    }, [message, onMessageCopy]);

    const sendFeedback = React.useCallback(
      async (liked: boolean, textFeedback: string) => {
        logger.info("Sending feedback", { liked: String(liked), textFeedback });
        if (!adminClient || !runId || !message || !messageID) {
          throw new Error("Missing adminClient, runId or message");
        }

        submitFeedback(adminClient, runId, messageID, liked, textFeedback);
      },
      [adminClient, runId, message, messageID, submitFeedback],
    );
    const handleSpeech = async () => {
      const isCurrentlySpeaking = speakingMessageId === messageID;

      try {
        if (isCurrentlySpeaking) {
          await stopSpeech();
        } else {
          await speak(message as string, messageID);
        }
      } catch (error) {
        // Check if it's a cancellation-related error
        if (error instanceof Error) {
          const errorMsg = error.message.toLowerCase();

          if (errorMsg.includes("cancel") || errorMsg.includes("interrupt")) {
            // Log cancellation as info, not as an error
            logger.info("Speech operation canceled", { message: error.message });

            return;
          }
        }

        // For SpeechSynthesisErrorEvent with canceled or interrupted
        if (error && typeof error === "object" && "error" in error) {
          if (error.error === "canceled" || error.error === "interrupted") {
            logger.info(`Speech ${error.error}`);

            return;
          }
        }

        // For other errors, log as warning but don't throw
        logger.warn("Error during speech operation");
      }
    };

    const handleFeedback = React.useCallback(
      async (liked: boolean) => {
        setRequestTextFeedback(true);
        setFeedback(liked ? "like" : "dislike");
        onFeedback?.(liked, userFeedbackText);
        sendFeedback(liked, userFeedbackText);
      },
      [onFeedback, sendFeedback, userFeedbackText],
    );

    const handleSubmitFeedback = async () => {
      // Invoke endpoint
      try {
        onFeedback?.(feedback === "like", userFeedbackText);
        sendFeedback(feedback === "like", userFeedbackText);
        setRequestTextFeedback(false);
        setUserFeedbackText("");
      } catch (error) {
        logger.error("Failed to submit feedback", error);
      }
    };

    const getInitials = (name: string) => {
      if (!name) return "";
      const names = name.split(" ");

      return names.map((n) => n[0]).join("");
    };

    return (
      <div
        {...props}
        ref={ref}
        className={cn("flex items-start gap-2 sm:gap-3 w-full pl-3 sm:pl-4 pr-2", className)}
      >
        <div className="relative flex-none mt-3">
          {showFeedback ? (
            <img
              alt=""
              className={`w-8 sm:w-10 ${brand.name === "Invest Ai" ? "invest-ai-icon-filter" : ""}`}
              src={avatar}
            />
          ) : (
            <Badge color="danger" placement="bottom-right">
              <Badge.Anchor>
                <div className="w-8 h-8 rounded-full bg-default-200 flex items-center justify-center text-xs font-semibold text-brand-primary flex-shrink-0 overflow-hidden">
                  {avatar ? (
                    <img
                      alt=""
                      className="w-full h-full object-cover"
                      src={avatar}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    getInitials(userName || "") || "?"
                  )}
                </div>
              </Badge.Anchor>
              {hasFailed && (
                <Badge.Label>
                  <Icon className="text-background" icon="gravity-ui:circle-exclamation-fill" />
                </Badge.Label>
              )}
            </Badge>
          )}
        </div>
        <div className="flex w-full flex-col gap-4 min-w-0">
          <div
            className={cn(
              "relative w-full rounded-2xl px-3 sm:px-4 py-3 text-default-600 transition-shadow",
              "shadow-[0_1px_2px_rgba(0,0,0,0.02),0_4px_16px_-8px_rgba(0,0,0,0.06)]",
              "dark:shadow-[0_1px_2px_rgba(0,0,0,0.2),0_4px_16px_-8px_rgba(0,0,0,0.25)]",
              "ring-1 ring-default-200/40 dark:ring-default-100/10",
              failedMessageClassName,
              messageClassName,
              agentMessageClassName,
            )}
          >
            {agent && (
              <div className={"pr-12 sm:pr-20 text-small"}>
                <p className="text-default-500">
                  {t("Agent")}: {agent.replace("_", " ")}
                </p>
              </div>
            )}
            {regenerationHook.error && (
              <div className="pr-12 sm:pr-20 text-small mb-2">
                <div className="bg-danger/10 border border-danger/20 rounded-md p-2 flex items-center gap-2">
                  <Icon
                    className="text-danger flex-shrink-0"
                    icon="gravity-ui:circle-exclamation-fill"
                  />
                  <span className="text-danger text-xs">{regenerationHook.error}</span>
                </div>
              </div>
            )}
            <div
              ref={messageRef}
              className={"pr-12 sm:pr-20 text-small overflow-hidden"}
              data-id={dataId}
              id={`message-${messageID}`}
            >
              {renderMessage(messageContent)}
            </div>
            {/* Excel Download Button */}
            {excelDownload && (
              <div className="mt-3" data-id="excel-download">
                <Button
                  className="gap-2"
                  size="sm"
                  variant="secondary"
                  onPress={() => {
                    try {
                      setExcelDownloadError(null);
                      downloadExcelFile(excelDownload.filename, excelDownload.data);
                    } catch (error) {
                      logger.error("Failed to download Excel file", error);
                      setExcelDownloadError(
                        error instanceof Error ? error.message : t("Download failed"),
                      );
                    }
                  }}
                >
                  <Icon icon="solar:download-linear" width={18} /> {t("Download Report")}:{" "}
                  {excelDownload.filename}
                </Button>
                {excelDownloadError && (
                  <div className="mt-2 text-danger text-xs flex items-center gap-1">
                    <Icon icon="gravity-ui:circle-exclamation-fill" width={14} />
                    {excelDownloadError}
                  </div>
                )}
              </div>
            )}
            {/* DOCX Download Button */}
            {docxDownload && (
              <div className="mt-3" data-id="docx-download">
                <Button
                  className="gap-2"
                  size="sm"
                  variant="secondary"
                  onPress={() => {
                    try {
                      setDocxDownloadError(null);
                      downloadDocxFile(docxDownload.filename, docxDownload.data);
                    } catch (error) {
                      logger.error("Failed to download DOCX file", error);
                      setDocxDownloadError(
                        error instanceof Error ? error.message : t("Download failed"),
                      );
                    }
                  }}
                >
                  <Icon icon="solar:download-linear" width={18} /> {t("Download Document")}:{" "}
                  {docxDownload.filename}
                </Button>
                {docxDownloadError && (
                  <div className="mt-2 text-danger text-xs flex items-center gap-1">
                    <Icon icon="gravity-ui:circle-exclamation-fill" width={14} />
                    {docxDownloadError}
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-between items-center pt-2">
              <div className="flex items-center gap-2">
                {
                  <Tooltip>
                    <TooltipTrigger>
                      <Button
                        isIconOnly
                        data-id={`save-prompt-button-${dataId}`}
                        size="sm"
                        variant="ghost"
                        onPress={handleSavePrompt}
                      >
                        <Icon className="text-lg text-default-600" icon="gravity-ui:floppy-disk" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("tooltip.saveAsPrompt")}</TooltipContent>
                  </Tooltip>
                }
                {showFeedback && !hasFailed && (
                  <div className="flex gap-1">
                    <Tooltip>
                      <TooltipTrigger>
                        <Button
                          isIconOnly
                          data-id={`copy-button-${dataId}`}
                          size="sm"
                          variant="ghost"
                          onPress={handleCopy}
                        >
                          {copied ? (
                            <Icon className="text-lg text-default-600" icon="gravity-ui:check" />
                          ) : (
                            <Icon className="text-lg text-default-600" icon="gravity-ui:copy" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t("tooltip.copy")}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger>
                        <Button
                          isIconOnly
                          data-id={`like-button-${dataId}`}
                          size="sm"
                          variant="ghost"
                          onPress={() => handleFeedback(true)}
                        >
                          {feedback === "like" ? (
                            <Icon
                              className="text-lg text-default-600"
                              icon="gravity-ui:thumbs-up-fill"
                            />
                          ) : (
                            <Icon
                              className="text-lg text-default-600"
                              icon="gravity-ui:thumbs-up"
                            />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t("tooltip.like")}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger>
                        <Button
                          isIconOnly
                          data-id={`dislike-button-${dataId}`}
                          size="sm"
                          variant="ghost"
                          onPress={() => handleFeedback(false)}
                        >
                          {feedback === "dislike" ? (
                            <Icon
                              className="text-lg text-default-600"
                              icon="gravity-ui:thumbs-down-fill"
                            />
                          ) : (
                            <Icon
                              className="text-lg text-default-600"
                              icon="gravity-ui:thumbs-down"
                            />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t("tooltip.dislike")}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger>
                        <Button
                          isIconOnly
                          data-id={`voice-button-${dataId}`}
                          size="sm"
                          variant="ghost"
                          onPress={() => handleSpeech()}
                        >
                          {speakingMessageId === messageID ? (
                            <Icon className="text-lg text-primary" icon="gravity-ui:volume" />
                          ) : (
                            <Icon className="text-lg text-default-600" icon="gravity-ui:volume" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t("tooltip.voice")}</TooltipContent>
                    </Tooltip>
                    {regenerationHook.loading ? (
                      <RegenerateButton
                        attempts={regenerationHook.attemptCount || 1}
                        dataId={dataId}
                        isLoading={regenerationHook.loading}
                        maxAttempts={3}
                        onRegenerate={handleRegenerate}
                      />
                    ) : status === "streaming" ? (
                      <Tooltip>
                        <TooltipTrigger>
                          <Button
                            isIconOnly
                            data-id={`stop-button-${dataId}`}
                            size="sm"
                            variant="ghost"
                            onPress={() => handleStop()}
                          >
                            <Icon className="text-lg text-default-600" icon="gravity-ui:square" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("tooltip.stop")}</TooltipContent>
                      </Tooltip>
                    ) : chatWorkflowId === "chat_agent" ? (
                      <RegenerateButton
                        attempts={regenerationHook.attemptCount || 1}
                        dataId={dataId}
                        isLoading={regenerationHook.loading}
                        maxAttempts={3}
                        onRegenerate={handleRegenerate}
                      />
                    ) : null}
                    <AttemptNavigation
                      currentAttempt={regenerationHook.currentAttempt || _currentAttempt}
                      isLoading={regenerationHook.loading}
                      show={(regenerationHook.attemptCount || _attempts) > 1}
                      totalAttempts={regenerationHook.attemptCount || _attempts}
                      onNavigate={handleAttemptChange}
                    />
                  </div>
                )}
              </div>

              {timestamp && (
                <span className="text-xs text-default-500 flex items-center gap-1">
                  <Icon className="text-default-400" icon="gravity-ui:clock" width={12} />
                  {formatTimestamp(timestamp)}
                </span>
              )}
              {/* TODO: Add usage */}
              {/* {usage && (
                <div className="flex gap-1">
                  <span className="text-tiny text-default-600 flex items-center">
                    <Icon className="mr-1" icon="gravity-ui:arrow-up" />
                    {usage.promptTokens}
                    <Icon className="ml-2 mr-1" icon="gravity-ui:arrow-down" />
                    {usage.completionTokens}
                  </span>
                </div>
              )} */}
            </div>
            {/* Show legacy sources only if no structured citations */}
            {!citations?.length && (sourcesFiles.length > 0 || sourcesWeb.length > 0) && (
              <MessageSources sourcesFiles={sourcesFiles} sourcesWeb={sourcesWeb} />
            )}
            {/* Citations are now inline clickable links in the message text via MessageWithCitations */}
          </div>
          {requestTextFeedback && (
            <div className="flex items-center gap-2 rounded-medium border-small border-default-100 shadow-small ml-6">
              <Input
                className="text-small text-default-600 flex-1"
                data-id="feedback-input"
                placeholder={t("What is your feedback on this response?")}
                size="md"
                value={userFeedbackText}
                variant="secondary"
                onChange={(e) => setUserFeedbackText(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && userFeedbackText) {
                    e.preventDefault();
                    handleSubmitFeedback();
                  }
                }}
              />
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    isIconOnly
                    color={!userFeedbackText ? "default" : "primary"}
                    data-id="feedback-submit-button"
                    isDisabled={!userFeedbackText}
                    type="submit"
                    variant="primary"
                    onPress={handleSubmitFeedback}
                  >
                    <Icon
                      className={cn(
                        "[&>path]:stroke-[2px]",
                        !userFeedbackText ? "text-default-600" : "text-primary-foreground",
                      )}
                      icon="solar:arrow-up-linear"
                      width={20}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("tooltip.submit")}</TooltipContent>
              </Tooltip>
            </div>
          )}
          {files && files.length > 0 && (
            <div className="flex flex-row gap-2 overflow-x-auto py-0">
              {files.map((file) => (
                <Tooltip key={file.id}>
                  <TooltipTrigger
                    aria-label={`Preview ${file.name}`}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-medium bg-default-100 min-w-fit hover:bg-default-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-300"
                    onClick={() => openPreviewForFile(file)}
                  >
                    <FileTypeIcon
                      className="text-default-500"
                      icon={convertFileTypeToIcon(file.type)}
                      width={16}
                    />
                    <span className="text-tiny text-default-600 truncate max-w-[150px]">
                      {file.name}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span className="break-all">{file.name}</span>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          )}
          <FilePreviewModal
            documentData={previewDocumentData}
            error={previewError}
            fileName={previewFile?.name}
            fileType={previewFile?.type}
            fileUrl={previewUrl || undefined}
            isLoading={previewLoading}
            isOpen={previewOpen}
            onDownload={downloadPreviewFile}
            onOpenChange={handlePreviewOpenChange}
          />
          <CitationDocumentViewer
            citation={selectedCitation}
            isOpen={citationViewerOpen}
            onOpenChange={setCitationViewerOpen}
          />
        </div>
      </div>
    );
  },
);

export { MessageCard };

MessageCard.displayName = "MessageCard";
